/**
 * API Service for Numera Web
 * Handles communication with the numera-server backend API
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { useTenantStore } from '@/stores/tenant'
import { useAuthStore } from '@/stores/auth'
import { performanceMonitor } from './performance/performanceMonitor'
import { cacheRequestInterceptor, cacheResponseInterceptor, cacheErrorInterceptor, configureApiCache } from './cache/apiCache'
import { performanceConfig } from '@/config/performance'

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'

class ApiService {
  private axiosInstance: AxiosInstance

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      method: 'GET', // Explicitly set default method
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Set additional defaults to prevent undefined values
    this.axiosInstance.defaults.method = 'GET'

    // Configure API caching
    if (performanceConfig.cache.enabled) {
      configureApiCache()
    }

    // Request interceptor to add auth headers and caching
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const authStore = useAuthStore()
        const tenantStore = useTenantStore()

        // Ensure method is always defined (axios sometimes doesn't set default)
        if (!config.method) {
          config.method = 'GET'
        }

        // Ensure URL is always defined
        if (!config.url) {
          throw new Error('Request URL is required')
        }

        // Add JWT token if available
        if (authStore.token) {
          config.headers.Authorization = `Bearer ${authStore.token}`
        }

        // Add tenant slug if available (backend expects slug, not ID)
        if (tenantStore.currentTenant?.slug) {
          config.headers['X-Tenant-ID'] = tenantStore.currentTenant.slug

          // Debug log for tenant context
          if (import.meta.env.DEV) {
            console.log('🏢 Adding X-Tenant-ID header:', {
              headerValue: tenantStore.currentTenant.slug,
              tenantId: tenantStore.currentTenant.id,
              tenantName: tenantStore.currentTenant.name,
              tenantSlug: tenantStore.currentTenant.slug
            })
          }
        } else {
          // Debug log when no tenant is available
          if (import.meta.env.DEV) {
            console.warn('⚠️ No current tenant slug available for API request:', {
              url: config.url,
              hasTenant: !!tenantStore.currentTenant,
              hasSlug: !!tenantStore.currentTenant?.slug,
              availableTenantsCount: tenantStore.availableTenants?.length || 0
            })
          }
        }

        // Track API call start time
        ;(config as any).metadata = { startTime: performance.now() }

        // Debug log for axios configuration
        if (import.meta.env.DEV) {
          console.log('🔍 Request Config Debug:', {
            method: config.method,
            url: config.url,
            baseURL: config.baseURL,
            headers: config.headers,
            hasData: !!config.data,
            hasParams: !!config.params
          })
        }

        // Temporarily disable cache interceptor to debug axios error
        // if (performanceConfig.cache.enabled) {
        //   const interceptedConfig = cacheRequestInterceptor(config)

        //   // Check if we got a cache hit
        //   if (interceptedConfig.__cacheHit) {
        //     // Return the cached response immediately without making a request
        //     const cacheHit = interceptedConfig.__cacheHit
        //     return Promise.resolve(cacheHit.response)
        //   }

        //   return interceptedConfig
        // }

        return config
      },
      (error) => {
        console.error('❌ Request Interceptor Error:', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor for error handling and performance tracking
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log successful API calls in development
        if (import.meta.env.DEV) {
          const method = response.config.method ? response.config.method.toUpperCase() : 'GET'
          console.log(`✅ API Success: ${method} ${response.config.url}`, {
            status: response.status,
            data: response.data,
          })
        }

        // Track API call performance
        const config = response.config as any
        if (config.metadata?.startTime) {
          const duration = performance.now() - config.metadata.startTime
          const trackingMethod = config.method ? config.method.toUpperCase() : 'GET'

          performanceMonitor.trackApiCall(
            config.url || '',
            trackingMethod,
            duration,
            response.status
          )
        }

        // Temporarily disable cache response interceptor to debug axios error
        // if (performanceConfig.cache.enabled) {
        //   return cacheResponseInterceptor(response)
        // }

        return response
      },
      async (error) => {
        // Temporarily disable cache error interceptor to debug axios error
        // if (performanceConfig.cache.enabled) {
        //   const cacheResult = cacheErrorInterceptor(error)
        //   // If cache interceptor handled it, return the result
        //   if (cacheResult !== Promise.reject(error)) {
        //     return cacheResult
        //   }
        // }

        // Log API errors (cache hits are now handled upstream)
        if (import.meta.env.DEV) {
          const method = error.config?.method ? error.config.method.toUpperCase() : 'UNKNOWN'
          const url = error.config?.url || 'UNKNOWN URL'

          console.error(`❌ API Error: ${method} ${url}`, {
            status: error.response?.status,
            message: error.response?.data?.message || error.message,
            fullError: error
          })
        }

        // Track API call performance even for errors
        const config = error.config as any
        if (config?.metadata?.startTime) {
          const duration = performance.now() - config.metadata.startTime
          const trackingMethod = config.method ? config.method.toUpperCase() : 'GET'

          performanceMonitor.trackApiCall(
            config.url || '',
            trackingMethod,
            duration,
            error.response?.status || 0
          )
        }

        if (error.response?.status === 429) {
          const retryAfter = error.response.headers?.['retry-after']
          if (retryAfter) {
            console.warn(`Rate limited. Retry after ${retryAfter}s`)
          }
        }

        // Handle 401 errors (unauthorized) — attempt token refresh before logout
        if (error.response?.status === 401) {
          const requestUrl = error.config?.url || ''
          const onLoginPage = window.location.pathname === '/login'
          const isLoginRequest = requestUrl.includes('/auth/login')
          const isRefreshRequest = requestUrl.includes('/auth/refresh')

          if (!isLoginRequest && !onLoginPage && !isRefreshRequest) {
            const authStore = useAuthStore()

            // Attempt silent token refresh once before forcing logout
            if (!(error.config as any)._retried && authStore.token) {
              (error.config as any)._retried = true
              try {
                await authStore.refreshToken()
                // Retry the original request with the new token
                if (authStore.token) {
                  error.config.headers.Authorization = `Bearer ${authStore.token}`
                  return apiService.axiosInstance.request(error.config)
                }
              } catch {
                // Refresh failed — proceed to logout
              }
            }

            // Only logout if refresh also failed or wasn't attempted
            if (!authStore.token) {
              authStore.logout()
              window.location.href = '/login'
            }
          }
        }

        return Promise.reject(error)
      }
    )
  }

  // Generic request method
  async request<T = any>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      // Ensure config has required properties
      const safeConfig = {
        ...config,
        method: config.method || 'GET',
        url: config.url || ''
      }

      if (import.meta.env.DEV) {
        console.log('🔍 Final Request Config:', {
          method: safeConfig.method,
          url: safeConfig.url,
          hasHeaders: !!safeConfig.headers
        })
      }

      return this.axiosInstance.request<T>(safeConfig)
    } catch (error: any) {
      console.error('❌ Axios Internal Error:', {
        message: error.message,
        stack: error.stack,
        config: config
      })
      throw error
    }
  }

  // HTTP Methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return this.axiosInstance.get<T>(url, config)
    } catch (error: any) {
      console.error('❌ GET Request Error:', {
        url,
        error: error.message,
        config
      })
      throw error
    }
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config)
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config)
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch<T>(url, data, config)
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config)
  }
}

// Create singleton instance
export const apiService = new ApiService()

// Authentication API
export const authApi = {
  login: (credentials: { email: string; password: string; rememberMe?: boolean }) =>
    apiService.post('/auth/login', {
      ...credentials,
      rememberMe: credentials.rememberMe || false
    }),

  logout: (refreshToken?: string) =>
    apiService.post('/auth/logout', refreshToken ? { refreshToken } : {}),

  refreshToken: (refreshToken: string) =>
    apiService.post('/auth/refresh', { refreshToken }),

  getProfile: () =>
    apiService.get('/auth/profile'),

  updateProfile: (profileData: { name?: string; email?: string; phone?: string }) =>
    apiService.put('/auth/profile', profileData),

  updatePreferences: (preferences: any) =>
    apiService.put('/auth/preferences', preferences),

  changePassword: (passwordData: { currentPassword: string; newPassword: string }) =>
    apiService.post('/auth/change-password', passwordData),

  /** Request a password-reset email (public, rate-limited). Always 200. */
  forgotPassword: (email: string) =>
    apiService.post('/auth/forgot-password', { email }),

  /** Consume a reset token and set a new password (public). */
  resetPassword: (data: { token: string; newPassword: string; confirmPassword: string }) =>
    apiService.post('/auth/reset-password', data),

  getSessions: () =>
    apiService.get('/auth/sessions'),

  revokeSession: (sessionId: string) =>
    apiService.delete(`/auth/sessions/${sessionId}`),

  uploadAvatar: (file: File) => {
    const formData = new FormData()
    formData.append('avatar', file)
    return apiService.post('/auth/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  deleteAvatar: () => apiService.delete('/auth/avatar'),
}

// Accounts API
export const accountsApi = {
  getAccounts: (params?: any) =>
    apiService.get('/accounts', { params }),

  getAccount: (id: string) =>
    apiService.get(`/accounts/${id}`),

  createAccount: (data: any) =>
    apiService.post('/accounts', data),

  updateAccount: (id: string, data: any) =>
    apiService.put(`/accounts/${id}`, data),

  deleteAccount: (id: string) =>
    apiService.delete(`/accounts/${id}`),

  getAccountsForItems: () =>
    apiService.get('/accounts/for-items'),
}

// Journal API
export const journalApi = {
  getJournalEntries: (params?: any) =>
    apiService.get('/journal-entries', { params }),

  getJournalEntry: (id: string) =>
    apiService.get(`/journal-entries/${id}`),

  createJournalEntry: (data: any) =>
    apiService.post('/journal-entries', data),

  // Un asiento contabilizado es inmutable: el servidor NO expone PUT ni
  // DELETE /journal-entries/:id (responden 404). La corrección contable
  // estándar es la reversión: crea un asiento contrapuesto con fecha de hoy
  // que deja sin efecto el original sin tocar el mayor histórico.
  reverseJournalEntry: (id: string, data?: { description?: string }) =>
    apiService.post(`/journal-entries/${id}/reverse`, data ?? {}),
}

// Invoices API
export const invoicesApi = {
  getInvoices: (params?: any) =>
    apiService.get('/invoices', { params }),

  getInvoice: (id: string) =>
    apiService.get(`/invoices/${id}`),

  createInvoice: (data: any) =>
    apiService.post('/invoices', data),

  updateInvoice: (id: string, data: any) =>
    apiService.put(`/invoices/${id}`, data),

  deleteInvoice: (id: string) =>
    apiService.delete(`/invoices/${id}`),

  // Cancelación con motivo opcional (audit trail + asiento de reversión).
  cancelInvoice: (id: string, reason?: string) =>
    apiService.post(`/invoices/${id}/cancel`, reason ? { reason } : {}),

  // Envío real al cliente: flip draft->sent + asiento de CxC/ingresos + email.
  // c123: el PUT /:id no acepta `status` (lo descartaba en silencio y la UI
  // mostraba éxito falso).
  sendInvoice: (id: string, data?: { email?: string; subject?: string; message?: string }) =>
    apiService.post(`/invoices/${id}/send`, data ?? {}),

  generateInvoicePDF: (id: string) =>
    apiService.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),

  // c306: asignar NCF (comprobante fiscal DGII) desde el rango autorizado.
  // El backend lo expone en POST /api/invoices/:id/generate-ncf
  // (routes/invoices.js) pero ninguna vista lo llamaba: facturas emitidas
  // quedaban sin NCF aunque el tenant tuviera rangos activos configurados.
  generateNcf: (id: string, sequence = 'B01') =>
    apiService.post(`/invoices/${id}/generate-ncf`, { sequence }),

  // Historial de pagos de la factura (c634): el endpoint GET
  // /api/invoices/:id/payments existía (routes/invoices.js, controller
  // getInvoicePayments) pero nada en la SPA lo consumía — InvoiceDetail no
  // mostraba el rastro de los cobros registrados.
  getInvoicePayments: (id: string) =>
    apiService.get(`/invoices/${id}/payments`),
}

// Bills API
export const billsApi = {
  getBills: (params?: any) =>
    apiService.get('/bills', { params }),

  getBill: (id: string) =>
    apiService.get(`/bills/${id}`),

  // Historial de pagos de la factura (c633): consumido por BillDetail para
  // que una factura pagada conserve su rastro de pagos visible.
  getBillPayments: (id: string) =>
    apiService.get(`/bills/${id}/payments`),

  createBill: (data: any) =>
    apiService.post('/bills', data),

  updateBill: (id: string, data: any) =>
    apiService.put(`/bills/${id}`, data),

  deleteBill: (id: string) =>
    apiService.delete(`/bills/${id}`),

  // Cancelación con motivo opcional (audit trail + asiento de reversión).
  cancelBill: (id: string, reason?: string) =>
    apiService.post(`/bills/${id}/cancel`, reason ? { reason } : {}),

  batchPayBills: (data: { billIds: string[]; date?: string; method?: string; reference?: string; bankAccount?: string }) =>
    apiService.post('/bills/batch-pay', data),

  previewLandedCost: (data: { items: unknown[]; landedCostAllocationMethod?: string }) =>
    apiService.post('/bills/preview-landed-cost', data),
}

// Landed Cost Allocation API (import cost capitalization documents)
export const landedCostsApi = {
  getAllocations: (params?: any) =>
    apiService.get('/landed-costs', { params }),

  getAllocation: (id: string) =>
    apiService.get(`/landed-costs/${id}`),

  getEligibleBills: () =>
    apiService.get('/landed-costs/eligible-bills'),

  preview: (data: any) =>
    apiService.post('/landed-costs/preview', data),

  createAllocation: (data: any) =>
    apiService.post('/landed-costs', data),

  updateAllocation: (id: string, data: any) =>
    apiService.put(`/landed-costs/${id}`, data),

  deleteAllocation: (id: string) =>
    apiService.delete(`/landed-costs/${id}`),

  postAllocation: (id: string) =>
    apiService.post(`/landed-costs/${id}/post`, {}),

  reverseAllocation: (id: string) =>
    apiService.post(`/landed-costs/${id}/reverse`, {}),
}

// Payments API
export const paymentsApi = {
  getPayments: (params?: any) =>
    apiService.get('/payments', { params }),

  getPayment: (id: string) =>
    apiService.get(`/payments/${id}`),

  createPayment: (data: any) =>
    apiService.post('/payments', data),

  updatePayment: (id: string, data: any) =>
    apiService.put(`/payments/${id}`, data),

  deletePayment: (id: string) =>
    apiService.delete(`/payments/${id}`),

  // Anula el pago (revierte asiento y saldos del lado server, c664).
  cancelPayment: (id: string, reason?: string) =>
    apiService.post(`/payments/${id}/cancel`, reason ? { reason } : {}),
}

// Clients API
export const clientsApi = {
  getClients: (params?: any) =>
    apiService.get('/clients', { params }),

  getClient: (id: string) =>
    apiService.get(`/clients/${id}`),

  createClient: (data: any) =>
    apiService.post('/clients', data),

  updateClient: (id: string, data: any) =>
    apiService.put(`/clients/${id}`, data),

  deleteClient: (id: string) =>
    apiService.delete(`/clients/${id}`),

  getClientMetrics: () =>
    apiService.get('/clients/metrics'),
}

// Items API (products & services catalog)
export const itemsApi = {
  getItems: (params?: Record<string, unknown>) =>
    apiService.get('/items', { params }),

  /** Single item by id — used by the `?view=<id>` search deep link, which must
   *  resolve regardless of which list page the item lives on. */
  getItem: (id: string) =>
    apiService.get(`/items/${id}`),

  createItem: (data: Record<string, unknown>) =>
    apiService.post('/items', data),

  updateItem: (id: string, data: Record<string, unknown>) =>
    apiService.put(`/items/${id}`, data),

  deleteItem: (id: string) =>
    apiService.delete(`/items/${id}`),

  getItemUoms: (itemId: string) =>
    apiService.get(`/items/${itemId}/uoms`),

  createItemUom: (itemId: string, data: Record<string, unknown>) =>
    apiService.post(`/items/${itemId}/uoms`, data),

  deleteItemUom: (itemId: string, uomId: string) =>
    apiService.delete(`/items/${itemId}/uoms/${uomId}`),
}

// Vendors API
export const vendorsApi = {
  getVendors: (params?: any) =>
    apiService.get('/vendors', { params }),

  /** Payables across ALL vendors — the list header cannot sum a single page. */
  getVendorStatistics: () =>
    apiService.get('/vendors/statistics'),

  getVendor: (id: string) =>
    apiService.get(`/vendors/${id}`),

  createVendor: (data: any) =>
    apiService.post('/vendors', data),

  updateVendor: (id: string, data: any) =>
    apiService.put(`/vendors/${id}`, data),

  deleteVendor: (id: string) =>
    apiService.delete(`/vendors/${id}`),
}

// Purchase Orders API
export const purchaseOrdersApi = {
  getPurchaseOrders: (params?: any) =>
    apiService.get('/purchase-orders', { params }),

  getPurchaseOrder: (id: string) =>
    apiService.get(`/purchase-orders/${id}`),

  createPurchaseOrder: (data: any) =>
    apiService.post('/purchase-orders', data),

  // c544: edición de OC en borrador (PUT /:id, sólo status draft)
  updatePurchaseOrder: (id: string, data: Record<string, unknown>) =>
    apiService.put(`/purchase-orders/${id}`, data),

  approvePurchaseOrder: (id: string, data?: any) =>
    apiService.post(`/purchase-orders/${id}/approve`, data),

  sendPurchaseOrder: (id: string, data?: any) =>
    apiService.post(`/purchase-orders/${id}/send`, data),

  receiveItems: (id: string, data: any) =>
    apiService.post(`/purchase-orders/${id}/receive`, data),

  convertToBill: (id: string, data?: any) =>
    apiService.post(`/purchase-orders/${id}/convert-to-bill`, data),

  cancelPurchaseOrder: (id: string, data: any) =>
    apiService.post(`/purchase-orders/${id}/cancel`, data),

  deletePurchaseOrder: (id: string) =>
    apiService.delete(`/purchase-orders/${id}`),
}

// Expenses API
export const expensesApi = {
  getExpenses: (params?: any) =>
    apiService.get('/expenses', { params }),

  getExpense: (id: string) =>
    apiService.get(`/expenses/${id}`),

  createExpense: (data: any) =>
    apiService.post('/expenses', data),

  updateExpense: (id: string, data: any) =>
    apiService.put(`/expenses/${id}`, data),

  markExpensePaid: (id: string, paidDate?: string) =>
    apiService.post(`/expenses/${id}/mark-paid`, { paidDate }),

  voidExpense: (id: string) =>
    apiService.post(`/expenses/${id}/void`),

  deleteExpense: (id: string) =>
    apiService.delete(`/expenses/${id}`),
}

// Financial Reports API
export const inventoryApi = {
  getLevels: (params?: Record<string, unknown>) => apiService.get('/inventory/levels', { params }),
  getMovements: (params?: Record<string, unknown>) => apiService.get('/inventory/movements', { params }),
  getSummary: () => apiService.get('/inventory/summary'),
  createAdjustment: (data: Record<string, unknown>) => apiService.post('/inventory/adjustment', data),
  createInbound: (data: Record<string, unknown>) => apiService.post('/inventory/inbound', data),
  createOutbound: (data: Record<string, unknown>) => apiService.post('/inventory/outbound', data),
  getValuation: (params?: Record<string, unknown>) => apiService.get('/inventory/valuation', { params })
}

export const reportsApi = {
  getIncomeStatement: (params?: any) =>
    apiService.get('/reports/income-statement', { params }),

  getBalanceSheet: (params?: any) =>
    apiService.get('/reports/balance-sheet', { params }),

  getCashFlowStatement: (params?: any) =>
    apiService.get('/reports/cash-flow', { params }),

  getTrialBalance: (params?: any) =>
    apiService.get('/accounts/trial-balance', { params }),

  // Aging reports
  getARAgingSummary: (params?: { asOf?: string }) =>
    apiService.get('/reports/ar-aging', { params }),

  getARAgingDetail: (params?: { asOf?: string; clientId?: string }) =>
    apiService.get('/reports/ar-aging/detail', { params }),

  getAPAgingSummary: (params?: { asOf?: string }) =>
    apiService.get('/reports/ap-aging', { params }),

  getAPAgingDetail: (params?: { asOf?: string; vendorId?: string }) =>
    apiService.get('/reports/ap-aging/detail', { params }),

  getGeneralLedger: (params?: any) =>
    apiService.get('/reports/general-ledger', { params }),
  getJournalReport: (params?: any) =>
    apiService.get('/reports/journal', { params }),
  getIncomeStatementByPeriod: (params?: any) =>
    apiService.get('/reports/income-statement/by-period', { params }),
  getComparativeBalanceSheet: (params?: any) =>
    apiService.get('/reports/balance-sheet/comparative', { params }),

  // Sales reports
  getSalesByCustomer: (params?: any) => apiService.get('/reports/sales/by-customer', { params }),
  getSalesByProduct: (params?: any) => apiService.get('/reports/sales/by-product', { params }),
  getSalesByCustomerDetail: (params?: any) => apiService.get('/reports/sales/by-customer-detail', { params }),
  getSalesByProductDetail: (params?: any) => apiService.get('/reports/sales/by-product-detail', { params }),

  // Purchase reports
  getPurchasesByVendor: (params?: any) => apiService.get('/reports/purchases/by-vendor', { params }),
  getUnpaidBills: (params?: any) => apiService.get('/reports/purchases/unpaid-bills', { params }),
  getVendorBalance: (params?: any) => apiService.get('/reports/purchases/vendor-balance', { params }),
  getPurchasesByVendorDetail: (params?: any) => apiService.get('/reports/purchases/by-vendor-detail', { params }),

  // Inventory reports
  getInventoryValuation: (params?: any) => apiService.get('/reports/inventory/valuation', { params }),
  getInventoryWorksheet: (params?: any) => apiService.get('/reports/inventory/worksheet', { params }),

  // ITBIS detail reports
  getItbisExceptions: (params?: any) => apiService.get('/reports/itbis/exceptions', { params }),
  getItbisByTaxCode: (params?: any) => apiService.get('/reports/itbis/by-tax-code', { params }),
}

// Transfers API (CC payoff, CC credit, account transfers)
export const transfersApi = {
  createTransfer: (data: {
    fromAccountId: string
    toAccountId: string
    amount: number
    date?: string
    description?: string
    referenceNumber?: string
    transferType?: 'cc_payoff' | 'cc_credit' | 'general'
  }) => apiService.post('/transfers', data),

  getTransfers: (params?: { transferType?: string; page?: number; limit?: number }) =>
    apiService.get('/transfers', { params }),
}

// Recurring templates API
export const recurringApi = {
  getTemplates: (params?: { templateType?: string; isActive?: string; page?: number; limit?: number }) =>
    apiService.get('/recurring', { params }),

  getTemplate: (id: string) =>
    apiService.get(`/recurring/${id}`),

  createTemplate: (data: any) =>
    apiService.post('/recurring', data),

  updateTemplate: (id: string, data: any) =>
    apiService.put(`/recurring/${id}`, data),

  toggleActive: (id: string) =>
    apiService.post(`/recurring/${id}/toggle`),

  deleteTemplate: (id: string) =>
    apiService.delete(`/recurring/${id}`),

  generateNow: () =>
    apiService.post('/recurring/generate'),
}

// Fixed Assets API
export const fixedAssetsApi = {
  getAssets: (params?: any) =>
    apiService.get('/fixed-assets', { params }),

  getAsset: (id: string) =>
    apiService.get(`/fixed-assets/${id}`),

  getSummary: () =>
    apiService.get('/fixed-assets/summary'),

  createAsset: (data: any) =>
    apiService.post('/fixed-assets', data),

  updateAsset: (id: string, data: any) =>
    apiService.put(`/fixed-assets/${id}`, data),

  disposeAsset: (id: string, data: any) =>
    apiService.post(`/fixed-assets/${id}/dispose`, data),

  runDepreciation: (year: number, month: number) =>
    apiService.post('/fixed-assets/depreciation/run', { year, month }),
}

// Sales Orders API
export const salesOrdersApi = {
  getSalesOrders: (params?: any) =>
    apiService.get('/sales-orders', { params }),

  getSalesOrder: (id: string) =>
    apiService.get(`/sales-orders/${id}`),

  createSalesOrder: (data: any) =>
    apiService.post('/sales-orders', data),

  updateSalesOrder: (id: string, data: any) =>
    apiService.put(`/sales-orders/${id}`, data),

  updateStatus: (id: string, status: string) =>
    apiService.post(`/sales-orders/${id}/status`, { status }),

  convertToInvoice: (id: string) =>
    apiService.post(`/sales-orders/${id}/convert-to-invoice`),
}

// Customer Advances API
export const customerAdvancesApi = {
  getAdvances: (params?: any) => apiService.get('/customer-advances', { params }),
  createAdvance: (data: any) => apiService.post('/customer-advances', data),
  applyToInvoice: (id: string, invoiceId: string, amount?: number) =>
    apiService.post(`/customer-advances/${id}/apply`, { invoiceId, amount }),
  deleteAdvance: (id: string) => apiService.delete(`/customer-advances/${id}`),
}

// Expense Claims API
export const expenseClaimsApi = {
  getClaims: (params?: any) => apiService.get('/expense-claims', { params }),
  getClaim: (id: string) => apiService.get(`/expense-claims/${id}`),
  createClaim: (data: any) => apiService.post('/expense-claims', data),
  updateClaim: (id: string, data: any) => apiService.put(`/expense-claims/${id}`, data),
  submitClaim: (id: string) => apiService.post(`/expense-claims/${id}/submit`),
  approveClaim: (id: string) => apiService.post(`/expense-claims/${id}/approve`),
  rejectClaim: (id: string, reason?: string) => apiService.post(`/expense-claims/${id}/reject`, { reason }),
  markReimbursed: (id: string, reimbursementAccountId?: string) =>
    apiService.post(`/expense-claims/${id}/reimburse`, { reimbursementAccountId }),
}

// ITBIS Reports API
export const itbisApi = {
  getITBISReports: (params?: any) =>
    apiService.get('/reports/itbis', { params }),
}

// Period Close API
//
// The server has no `/periods` resource: a period is implicit (every month is
// open unless a `period_closes` row says otherwise), and the only endpoints are
// under `/period-close`. This client used to call `/periods*`, which 404'd on
// every request and left the whole Cierre de períodos screen unusable.
export const periodCloseApi = {
  // Closed/reopened period records. Query: { year?, type? }
  getClosedPeriods: (params?: { year?: number; type?: 'monthly' | 'yearly' }) =>
    apiService.get('/period-close', { params }),

  // Whether a given YYYY-MM-DD falls inside a closed period.
  checkDate: (date: string) =>
    apiService.get(`/period-close/check/${date}`),

  closeMonth: (data: { year: number; month: number; reason?: string }) =>
    apiService.post('/period-close/month', data),

  closeYear: (data: { year: number; reason?: string }) =>
    apiService.post('/period-close/year', data),

  // `reason` is required by the server and must be 10-1000 characters.
  reopenPeriod: (data: { periodCloseId: string; reason: string }) =>
    apiService.post('/period-close/reopen', data),
}

// Bank Reconciliation API (mounted at /api/bank-reconciliation on server)
const bankReconBase = '/bank-reconciliation'

export const bankReconciliationApi = {
  // Bank account management
  getBankAccounts: (params?: any) =>
    apiService.get(`${bankReconBase}/bank-accounts`, { params }),

  getBankAccount: (id: string) =>
    apiService.get(`${bankReconBase}/bank-accounts/${id}`),

  createBankAccount: (data: any) =>
    apiService.post(`${bankReconBase}/bank-accounts`, data),

  updateBankAccount: (id: string, data: any) =>
    apiService.put(`${bankReconBase}/bank-accounts/${id}`, data),

  deleteBankAccount: (id: string) =>
    apiService.delete(`${bankReconBase}/bank-accounts/${id}`),

  // Transacciones bancarias — rutas REALES del server (c301): el bloque
  // anterior apuntaba a /bank-accounts/:id/statements, /bank-statements/:id/*,
  // /reconciliation-rules, etc., que nunca existieron en el backend.
  getBankTransactions: (bankAccountId?: string, isReconciled?: boolean) =>
    apiService.get(`${bankReconBase}/bank-transactions`, {
      params: {
        bankAccountId,
        // El server compara el query crudo contra 'true' (string).
        isReconciled: isReconciled === undefined ? undefined : String(isReconciled)
      }
    }),

  importBankTransactions: (bankAccountId: string, transactions: Array<{
    date: string
    description: string
    amount: number
    type?: string
    reference?: string
  }>) =>
    apiService.post(`${bankReconBase}/bank-transactions/import`, { bankAccountId, transactions }),

  reconcileTransaction: (transactionId: string, matchedTransactionId?: string, matchType?: string) =>
    apiService.post(`${bankReconBase}/bank-transactions/${transactionId}/reconcile`, {
      matchedTransactionId,
      matchType
    }),

  // Conciliaciones registradas (POST/GET /bank-reconciliations)
  createReconciliation: (data: Record<string, unknown>) =>
    apiService.post(`${bankReconBase}/bank-reconciliations`, data),

  getReconciliations: (bankAccountId?: string) =>
    apiService.get(`${bankReconBase}/bank-reconciliations`, { params: { bankAccountId } }),
}

// User Management API
export const userManagementApi = {
  // Users CRUD
  getUsers: (params?: {
    page?: number
    limit?: number
    search?: string
    role?: string
    status?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }) =>
    apiService.get('/users', { params }),

  getUser: (id: string) =>
    apiService.get(`/users/${id}`),

  createUser: (data: any) =>
    apiService.post('/users', data),

  updateUser: (id: string, data: any) =>
    apiService.put(`/users/${id}`, data),

  deleteUser: (id: string) =>
    apiService.delete(`/users/${id}`),

  resetUserPassword: (id: string) =>
    apiService.post(`/users/${id}/reset-password`),

  // User status management
  activateUser: (id: string) =>
    apiService.post(`/users/${id}/activate`),

  deactivateUser: (id: string) =>
    apiService.post(`/users/${id}/deactivate`),

  suspendUser: (id: string, reason?: string) =>
    apiService.post(`/users/${id}/suspend`, { reason }),

  getUserProfile: (id: string) =>
    apiService.get(`/users/${id}/profile`),

  updateUserProfile: (id: string, data: any) =>
    apiService.put(`/users/${id}/profile`, data),

  // Password management
  changePassword: (data: any) =>
    apiService.post('/users/change-password', data),

  setupTwoFactor: () =>
    apiService.post('/users/setup-2fa'),

  bulkUpdateUsers: (data: { ids: string[]; updates: any }) =>
    apiService.post('/users/bulk-update', data),

  bulkDeleteUsers: (ids: string[]) =>
    apiService.post('/users/bulk-delete', { ids }),
}

// Report Drill-down API
export const drillDownApi = {
  // Account drill-down
  getAccountDrillDown: (params: {
    accountId?: string
    accountCode?: string
    startDate: string
    endDate: string
    page?: number
    limit?: number
    transactionType?: string
    minAmount?: number
    maxAmount?: number
    searchQuery?: string
  }) =>
    apiService.get('/reports/drill-down/account', { params }),

  // Transaction detail
  getTransactionDetail: (transactionId: string, transactionType: string) =>
    apiService.get(`/reports/drill-down/transaction/${transactionType}/${transactionId}`),

  // Account balance history
  getAccountBalanceHistory: (accountId: string, startDate: string, endDate: string) =>
    apiService.get(`/reports/drill-down/account/${accountId}/balance-history`, {
      params: { startDate, endDate }
    }),

  // Related transactions (for a specific transaction)
  getRelatedTransactions: (transactionId: string, transactionType: string) =>
    apiService.get(`/reports/drill-down/transaction/${transactionType}/${transactionId}/related`),
}

// Notes Templates API
export const notesTemplatesApi = {
  // Get all templates
  getTemplates: (params?: {
    page?: number
    limit?: number
    search?: string
    category?: 'invoice' | 'estimate' | 'general'
    active?: boolean
    isDefault?: boolean
  }) =>
    apiService.get('/notes-templates', { params }),

  // Get single template
  getTemplate: (id: string) =>
    apiService.get(`/notes-templates/${id}`),

  // Get default template for category
  getDefaultTemplate: (category: 'invoice' | 'estimate' | 'general') =>
    apiService.get(`/notes-templates/defaults/${category}`),

  // Create new template
  createTemplate: (data: {
    name: string
    content: string
    category?: 'invoice' | 'estimate' | 'general'
    isDefault?: boolean
  }) =>
    apiService.post('/notes-templates', data),

  // Update template
  updateTemplate: (id: string, data: {
    name?: string
    content?: string
    category?: 'invoice' | 'estimate' | 'general'
    isDefault?: boolean
    isActive?: boolean
  }) =>
    apiService.put(`/notes-templates/${id}`, data),

  // Delete template
  deleteTemplate: (id: string) =>
    apiService.delete(`/notes-templates/${id}`),

  // Duplicate template
  duplicateTemplate: (id: string, name?: string) =>
    apiService.post(`/notes-templates/${id}/duplicate`, { name })
}

// Attachments API
export const attachmentsApi = {
  // Get all attachments for an entity
  getAttachments: (
    // c116: el server acepta 'credit_note' (ATTACHABLE_MODELS) — sin él la
    // llamada tipada desde una nota de crédito no compila.
    attachableType: 'invoice' | 'estimate' | 'bill' | 'expense' | 'purchase_order' | 'debit_note' | 'credit_note',
    attachableId: string,
    params?: {
      page?: number
      limit?: number
      search?: string
      mimeType?: string
    }
  ) =>
    apiService.get(`/attachments/${attachableType}/${attachableId}`, { params }),

  uploadAttachments: (
    // c116: el server acepta 'credit_note' (ATTACHABLE_MODELS) — sin él la
    // llamada tipada desde una nota de crédito no compila.
    attachableType: 'invoice' | 'estimate' | 'bill' | 'expense' | 'purchase_order' | 'debit_note' | 'credit_note',
    attachableId: string,
    formData: FormData
  ) =>
    apiService.post(`/attachments/${attachableType}/${attachableId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),

  // Update attachment metadata
  updateAttachment: (
    id: string,
    data: {
      description?: string
      isPublic?: boolean
    }
  ) =>
    apiService.put(`/attachments/${id}`, data),

  // Delete attachment
  deleteAttachment: (id: string) =>
    apiService.delete(`/attachments/${id}`),

  // Download attachment
  downloadAttachment: (id: string) =>
    apiService.get(`/attachments/${id}/download`, { responseType: 'blob' }),

  // Get attachment statistics
  getAttachmentStats: (
    // c116: el server acepta 'credit_note' (ATTACHABLE_MODELS) — sin él la
    // llamada tipada desde una nota de crédito no compila.
    attachableType: 'invoice' | 'estimate' | 'bill' | 'expense' | 'purchase_order' | 'debit_note' | 'credit_note',
    attachableId: string
  ) =>
    apiService.get(`/attachments/stats/${attachableType}/${attachableId}`)
}

// Sales Receipts API
export const salesReceiptsApi = {
  getSalesReceipts: (params?: any) =>
    apiService.get('/sales-receipts', { params }),
  createSalesReceipt: (data: any) =>
    apiService.post('/sales-receipts', data),
  voidSalesReceipt: (id: string) =>
    apiService.post(`/sales-receipts/${id}/void`),
  deleteSalesReceipt: (id: string) =>
    apiService.delete(`/sales-receipts/${id}`),
}

// Vendor Credits API
export const vendorCreditsApi = {
  getVendorCredits: (params?: any) =>
    apiService.get('/vendor-credits', { params }),
  createVendorCredit: (data: any) =>
    apiService.post('/vendor-credits', data),
  issueVendorCredit: (id: string) =>
    apiService.post(`/vendor-credits/${id}/issue`),
  applyToBill: (id: string, data: any) =>
    apiService.post(`/vendor-credits/${id}/apply`, data),
  deleteVendorCredit: (id: string) =>
    apiService.delete(`/vendor-credits/${id}`),
}

// Bank Deposits API
export const bankDepositsApi = {
  getPendingPayments: () =>
    apiService.get('/bank-deposits/pending'),
  getDeposits: (params?: any) =>
    apiService.get('/bank-deposits', { params }),
  // Detalle con la composición completa: Items (monto/descripción/cuenta de
  // origen), BankAccount y JournalEntry del asiento generado.
  getDeposit: (id: string) =>
    apiService.get(`/bank-deposits/${id}`),
  createDeposit: (data: any) =>
    apiService.post('/bank-deposits', data),
  voidDeposit: (id: string) =>
    apiService.post(`/bank-deposits/${id}/void`),
}

// Refund Receipts API
export const refundReceiptsApi = {
  getRefundReceipts: (params?: any) =>
    apiService.get('/refund-receipts', { params }),
  createRefundReceipt: (data: any) =>
    apiService.post('/refund-receipts', data),
  voidRefundReceipt: (id: string) =>
    apiService.post(`/refund-receipts/${id}/void`),
  deleteRefundReceipt: (id: string) =>
    apiService.delete(`/refund-receipts/${id}`),
}

// Delayed Charges API
export const delayedChargesApi = {
  getDelayedCharges: (params?: any) =>
    apiService.get('/delayed-charges', { params }),
  createDelayedCharge: (data: any) =>
    apiService.post('/delayed-charges', data),
  updateDelayedCharge: (id: string, data: any) =>
    apiService.put(`/delayed-charges/${id}`, data),
  deleteDelayedCharge: (id: string) =>
    apiService.delete(`/delayed-charges/${id}`),
  // c349: convierte un cargo diferido pendiente en factura (POST /:id/invoice)
  invoiceDelayedCharge: (id: string) =>
    apiService.post(`/delayed-charges/${id}/invoice`),
}

// Payroll – Employees API
export const payrollEmployeesApi = {
  getEmployees: (params?: any) =>
    apiService.get('/payroll/employees', { params }),
  getEmployee: (id: string) =>
    apiService.get(`/payroll/employees/${id}`),
  createEmployee: (data: any) =>
    apiService.post('/payroll/employees', data),
  updateEmployee: (id: string, data: any) =>
    apiService.put(`/payroll/employees/${id}`, data),
  deleteEmployee: (id: string) =>
    apiService.delete(`/payroll/employees/${id}`),
  getDepartments: () =>
    apiService.get('/payroll/employees/departments'),
}

// Payroll – Tax Configuration & Calculator API
export const payrollTaxApi = {
  getConfig: (year?: number) =>
    apiService.get('/payroll/tax-config', { params: year ? { year } : {} }),
  updateConfig: (year: number, data: any) =>
    apiService.put(`/payroll/tax-config/${year}`, data),
  calculate: (data: { grossSalary?: number; employeeIds?: string[]; year?: number }) =>
    apiService.post('/payroll/calculate', data),
}

// Payroll – Dashboard API
export const payrollDashboardApi = {
  getDashboard: () => apiService.get('/payroll/dashboard'),
}

// Payroll – Tax Forms API
export const payrollTaxFormsApi = {
  getForm609: (period: string) =>
    apiService.get('/payroll/tax-forms/609', { params: { period } }),
  getForm492: (year: number) =>
    apiService.get('/payroll/tax-forms/492', { params: { year } }),
  getTSSReport: (period: string) =>
    apiService.get('/payroll/tax-forms/tss', { params: { period } }),
}

// Payroll – Runs API
export const payrollRunsApi = {
  getRuns: (params?: any) =>
    apiService.get('/payroll/runs', { params }),
  getRun: (id: string) =>
    apiService.get(`/payroll/runs/${id}`),
  createRun: (data: { periodMonth: number; periodYear: number; notes?: string }) =>
    apiService.post('/payroll/runs', data),
  calculateRun: (id: string) =>
    apiService.post(`/payroll/runs/${id}/calculate`),
  postRun: (id: string, paymentAccountId?: string) =>
    apiService.post(`/payroll/runs/${id}/post`, { paymentAccountId }),
  markPaid: (id: string, data: { paymentAccountId: string; paymentDate?: string; paymentMethod?: string; paymentReference?: string }) =>
    apiService.post(`/payroll/runs/${id}/mark-paid`, data),
  deleteRun: (id: string) =>
    apiService.delete(`/payroll/runs/${id}`),
}

// Payroll – Timesheets API
export const payrollTimesheetsApi = {
  getTimesheets: (params?: any) =>
    apiService.get('/payroll/timesheets', { params }),
  getTimesheet: (id: string) =>
    apiService.get(`/payroll/timesheets/${id}`),
  createTimesheet: (data: any) =>
    apiService.post('/payroll/timesheets', data),
  updateTimesheet: (id: string, data: any) =>
    apiService.put(`/payroll/timesheets/${id}`, data),
  submitTimesheet: (id: string) =>
    apiService.post(`/payroll/timesheets/${id}/submit`),
  approveTimesheet: (id: string) =>
    apiService.post(`/payroll/timesheets/${id}/approve`),
  rejectTimesheet: (id: string, reason?: string) =>
    apiService.post(`/payroll/timesheets/${id}/reject`, { reason }),
  deleteTimesheet: (id: string) =>
    apiService.delete(`/payroll/timesheets/${id}`),
}

export const creditNotesApi = {
  getAll: (params?: any) => apiService.get('/credit-notes', { params }),
  getById: (id: string) => apiService.get(`/credit-notes/${id}`),
  create: (data: any) => apiService.post('/credit-notes', data),
  update: (id: string, data: any) => apiService.put(`/credit-notes/${id}`, data),
  apply: (id: string) => apiService.post(`/credit-notes/${id}/apply`),
  void: (id: string, data?: any) => apiService.post(`/credit-notes/${id}/void`, data),
  delete: (id: string) => apiService.delete(`/credit-notes/${id}`),
}

export const debitNotesApi = {
  getAll: (params?: any) => apiService.get('/debit-notes', { params }),
  getById: (id: string) => apiService.get(`/debit-notes/${id}`),
  create: (data: any) => apiService.post('/debit-notes', data),
  update: (id: string, data: any) => apiService.put(`/debit-notes/${id}`, data),
  apply: (id: string) => apiService.post(`/debit-notes/${id}/apply`),
  void: (id: string, data?: any) => apiService.post(`/debit-notes/${id}/void`, data),
  delete: (id: string) => apiService.delete(`/debit-notes/${id}`),
}

export const notificationsApi = {
  list: () => apiService.get('/notifications'),
  markRead: (id: string) => apiService.patch(`/notifications/${id}/read`),
  markAllRead: () => apiService.post('/notifications/mark-all-read'),
}

export const ncfApi = {
  getRanges: (params?: any) => apiService.get('/ncf/ranges', { params }),
  createRange: (data: any) => apiService.post('/ncf/ranges', data),
  updateRange: (id: string, data: any) => apiService.put(`/ncf/ranges/${id}`, data),
  deleteRange: (id: string) => apiService.delete(`/ncf/ranges/${id}`),
  getNextNumber: (sequenceCode: string) => apiService.get(`/ncf/next/${sequenceCode}`),
}

export const taxWithholdingsApi = {
  getAll: (params?: any) => apiService.get('/tax-withholdings', { params }),
  getById: (id: string) => apiService.get(`/tax-withholdings/${id}`),
  getStats: (params?: any) => apiService.get('/tax-withholdings/stats', { params }),
  getTypes: () => apiService.get('/tax-withholdings/types'),
  getDgiiReport: (params?: any) => apiService.get('/tax-withholdings/dgii-report', { params }),
  create: (data: any) => apiService.post('/tax-withholdings', data),
  update: (id: string, data: any) => apiService.put(`/tax-withholdings/${id}`, data),
  // Documento fiscal: no existe DELETE en el servidor; la corrección es anular.
  void: (id: string, data: { reason: string }) => apiService.post(`/tax-withholdings/${id}/void`, data),
}

export const bankFeedsApi = {
  importFile: (formData: FormData) => apiService.post('/bank-feeds/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getPending: (params?: any) => apiService.get('/bank-feeds/pending', { params }),
  matchTransaction: (data: any) => apiService.post('/bank-feeds/match', data),
  batchCategorize: () => apiService.post('/bank-feeds/batch-categorize'),
}

export const budgetsApi = {
  getAll: (params?: any) => apiService.get('/budgets', { params }),
  getById: (id: string) => apiService.get(`/budgets/${id}`),
  create: (data: any) => apiService.post('/budgets', data),
  update: (id: string, data: any) => apiService.put(`/budgets/${id}`, data),
  delete: (id: string) => apiService.delete(`/budgets/${id}`),
  getVsActual: (id: string, params?: any) => apiService.get(`/budgets/${id}/vs-actual`, { params }),
}

export const bankRulesApi = {
  getAll: () => apiService.get('/bank-rules'),
  create: (data: any) => apiService.post('/bank-rules', data),
  update: (id: string, data: any) => apiService.put(`/bank-rules/${id}`, data),
  delete: (id: string) => apiService.delete(`/bank-rules/${id}`),
  apply: () => apiService.post('/bank-rules/apply'),
}

// Global Search API
export const searchApi = {
  search: (params: { q: string; types?: string; limit?: number }) =>
    apiService.get('/search', { params }),
}

// ===== Projects API =====
export const projectsApi = {
  // CRUD
  getProjects: (params?: any) =>
    apiService.get('/projects', { params }),
  getProject: (id: string) =>
    apiService.get(`/projects/${id}`),
  createProject: (data: any) =>
    apiService.post('/projects', data),
  updateProject: (id: string, data: any) =>
    apiService.put(`/projects/${id}`, data),
  deleteProject: (id: string) =>
    apiService.delete(`/projects/${id}`),
  changeStatus: (id: string, status: string) =>
    apiService.patch(`/projects/${id}/status`, { status }),
  createMaterialUsage: (id: string, data: any) =>
    apiService.post(`/projects/${id}/material-usage`, data),
  getMaterialUsage: (id: string) =>
    apiService.get(`/projects/${id}/material-usage`),
  createProgressInvoice: (id: string, data?: any) =>
    apiService.post(`/projects/${id}/progress-invoice`, data || {}),
  releaseRetainage: (id: string) =>
    apiService.post(`/projects/${id}/release-retainage`),
  getBillingStatus: (id: string) =>
    apiService.get(`/projects/${id}/billing-status`),
  getProfitLoss: (id: string, params?: any) =>
    apiService.get(`/projects/${id}/profit-loss`, { params }),
  getWipReport: () =>
    apiService.get('/projects/wip'),
  getCashFlow: (id: string, params?: any) =>
    apiService.get(`/projects/${id}/cash-flow`, { params }),
  getCostCenters: (id: string) =>
    apiService.get(`/projects/${id}/cost-centers`),
  getCostsSummary: (id: string) =>
    apiService.get(`/projects/${id}/costs/summary`),
}

export default apiService
